import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import type { Recipient } from '@/types/roc/models/Recipient';
import { RecipientStatus as RecipientStatusEnum } from '@/types/roc/models/RecipientStatus';
import {
  ReferralReferralTypeEnum,
  ReferralStatusEnum
} from '@/types/roc/models/Referral';
import { getFromNowFormattedDate } from '@/utils/date';

dayjs.extend(utc);

export type ReferralStatus = ReferralStatusEnum;
export type RecipientStatus = RecipientStatusEnum;
export type ReferralType = ReferralReferralTypeEnum;

export type { Recipient as OutboundReferralRecipient };

export interface OutboundReferralItem {
  referral_id: string;
  sender_facility_id: string;
  status: ReferralStatus;
  referral_type: ReferralType;
  first_name: string | null;
  last_name: string | null;
  dob: string | null;
  target_discharge_date: string | null;
  sent_at: string | null;
  confirmed_at: string | null;
  closed_at: string | null;
  latest_activity_at?: string | null;
  recipients: Recipient[];
  has_unread?: boolean;
}

export interface OutboundReferralsResponse {
  items: OutboundReferralItem[];
  cursor: string | null;
}

export interface OutboundReferralGridRow {
  id: string;
  referral_id: string;
  first_name: string | null;
  last_name: string | null;
  referral_type: ReferralType;
  target_discharge_date: string | null;
  referral_status: ReferralStatus;
  sender_facility_id: string;
  recipient_facility_id: string;
  recipient_facility_name: string | null;
  recipient_status: RecipientStatus;
  last_activity_at: string | null;
  has_unread: boolean;
}

export const REFERRAL_TYPE_LABELS: Record<ReferralType, string> = {
  [ReferralReferralTypeEnum.Snf]: 'SNF',
  [ReferralReferralTypeEnum.Dialysis]: 'Dialysis',
  [ReferralReferralTypeEnum.HomeHealth]: 'Home Health',
  [ReferralReferralTypeEnum.Ltac]: 'LTAC',
  [ReferralReferralTypeEnum.Dme]: 'DME'
};

export class OutboundReferralModel implements OutboundReferralItem {
  referral_id: string;
  sender_facility_id: string;
  status: ReferralStatus;
  referral_type: ReferralType;
  first_name: string | null;
  last_name: string | null;
  dob: string | null;
  target_discharge_date: string | null;
  sent_at: string | null;
  confirmed_at: string | null;
  closed_at: string | null;
  latest_activity_at: string | null;
  recipients: Recipient[];
  has_unread: boolean;

  constructor(data: OutboundReferralItem) {
    this.referral_id = data.referral_id;
    this.sender_facility_id = data.sender_facility_id;
    this.status = data.status;
    this.referral_type = data.referral_type;
    this.first_name = data.first_name;
    this.last_name = data.last_name;
    this.dob = data.dob;
    this.target_discharge_date = data.target_discharge_date;
    this.sent_at = data.sent_at;
    this.confirmed_at = data.confirmed_at;
    this.closed_at = data.closed_at;
    this.latest_activity_at = data.latest_activity_at ?? null;
    this.recipients = data.recipients ?? [];
    this.has_unread = data.has_unread ?? false;
  }

  get fullName(): string {
    return [this.first_name, this.last_name].filter(Boolean).join(' ') || '—';
  }

  get formattedMovedOutDate(): string {
    if (!this.target_discharge_date) {
      return '—';
    }
    return dayjs.utc(this.target_discharge_date).format('MMM D, YYYY');
  }

  get lastActivityAt(): string | null {
    const timestamps = [
      this.sent_at,
      this.confirmed_at,
      this.closed_at,
      this.latest_activity_at
    ].filter((t): t is string => t !== null);
    if (!timestamps.length) {
      return null;
    }
    return timestamps.reduce((latest, ts) =>
      dayjs.utc(ts).isAfter(dayjs.utc(latest)) ? ts : latest
    );
  }

  get lastActivityLabel(): string {
    if (!this.lastActivityAt) {
      return '—';
    }
    return getFromNowFormattedDate(this.lastActivityAt);
  }

  get referralTypeLabel(): string {
    return REFERRAL_TYPE_LABELS[this.referral_type] ?? this.referral_type;
  }

  toGridRows(): OutboundReferralGridRow[] {
    if (this.recipients.length === 0) {
      return [
        {
          id: this.referral_id,
          referral_id: this.referral_id,
          first_name: this.first_name,
          last_name: this.last_name,
          referral_type: this.referral_type,
          target_discharge_date: this.target_discharge_date,
          referral_status: this.status,
          sender_facility_id: this.sender_facility_id,
          recipient_facility_id: '',
          recipient_facility_name: null,
          recipient_status: RecipientStatusEnum.Pending,
          last_activity_at: this.lastActivityAt,
          has_unread: this.has_unread
        }
      ];
    }
    return this.recipients.map((recipient) => ({
      id: `${this.referral_id}__${recipient.facility_id}`,
      referral_id: this.referral_id,
      first_name: this.first_name,
      last_name: this.last_name,
      referral_type: this.referral_type,
      target_discharge_date: this.target_discharge_date,
      referral_status: this.status,
      sender_facility_id: this.sender_facility_id,
      recipient_facility_id: recipient.facility_id,
      recipient_facility_name: recipient.facility_name?.trim() || null,
      recipient_status: recipient.status,
      last_activity_at: this.lastActivityAt,
      has_unread: recipient.has_unread ?? false
    }));
  }
}
